import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import VendorActionBar from '../../components/ui/VendorActionBar';
import VendorHero from './components/VendorHero';
import VendorInfo from './components/VendorInfo';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import ReviewsSection from './components/ReviewsSection';
import PortfolioGallery from './components/PortfolioGallery';
import PricingPackages from './components/PricingPackages';
import LocationMap from './components/LocationMap';
import BookingModal from './components/BookingModal';
import { shareContent } from '../../utils/sharingUtils';
import { chatService } from '../../services/vendorService';
import vendorDataService from '../../services/vendorDataService';
import reviewService from '../../services/reviewService';
import savedVendorsService from '../../services/savedVendorsService';
import inquiryService from '../../services/inquiryService';
import viewingHistoryService from '../../services/viewingHistoryService';
import bookingService from '../../services/bookingService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../constants';

const VendorProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const { showToast } = useToast();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [shareStatus, setShareStatus] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    contactInfo: {
      phone: '',
      preferredDate: '',
      eventDate: '',
      guestCount: '',
      budget: ''
    }
  });
  const [messageSending, setMessageSending] = useState(false);
  const [messageStatus, setMessageStatus] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Load vendor data from Supabase
  useEffect(() => {
    const loadVendorData = async () => {
      setLoading(true);
      try {
        // Get vendor ID from URL search params, location state, or route params
        const vendorId = searchParams.get('id') || location.state?.vendorId || params.vendorId;

        if (!vendorId) {
          console.error('No vendor ID provided');
          navigate('/category-browse');
          return;
        }

        // Fetch vendor details
        const vendorResult = await vendorDataService.getVendorById(vendorId);

        if (!vendorResult.success || !vendorResult.data) {
          console.error('Vendor not found');
          navigate('/category-browse');
          return;
        }

        // Transform vendor data to match component expectations
        const vendorData = vendorResult.data;
        const transformedVendor = {
          id: vendorData.id,
          name: vendorData.business_name,
          rating: vendorData.average_rating || 0,
          reviewCount: vendorData.review_count || 0,
          bookingCount: vendorData.total_bookings || 0,
          location: `${vendorData.city}, ${vendorData.state}`,
          serviceRadius: 50,
          businessAddress: vendorData.business_address || `${vendorData.city}, ${vendorData.state}`,
          coordinates: vendorData.coordinates || { lat: 37.7749, lng: -122.4194 },
          responseTime: vendorData.response_time || '24 hours',
          isVerified: vendorData.is_verified,
          isAvailable: true,
          description: vendorData.description,
          services: vendorData.services || [],
          culturalSpecializations: vendorData.cultural_specialties?.map(spec => ({
            name: spec,
            description: spec
          })) || [],
          languages: vendorData.languages || ['English'],
          pricing: {
            startingPrice: vendorData.starting_price,
            description: 'Full service package',
            duration: '8-10 hours',
            guestCount: 300
          },
          tags: [
            vendorData.is_verified && 'Verified',
            vendorData.is_featured && 'Featured',
            'Free Consultation',
            vendorData.subscription_type === 'premium' && 'Premium Vendor'
          ].filter(Boolean),
          gallery: vendorData.vendor_gallery?.map(g => g.image_url) || [],
          packages: vendorData.vendor_packages || [],
          availability: {
            availableDates: [],
            bookedDates: [],
            blockedDates: []
          }
        };

        setVendor(transformedVendor);

        // Fetch reviews
        const reviewsResult = await reviewService.getVendorReviews(vendorId, { limit: 10 });
        if (reviewsResult.success) {
          setReviews(reviewsResult.data);
        }

        // Check if vendor is saved (if user is logged in)
        if (user?.id) {
          const savedResult = await savedVendorsService.isVendorSaved(user.id, vendorId);
          if (savedResult.success) {
            setIsFavorited(savedResult.isSaved);
          }
        }
      } catch (error) {
        console.error('Error loading vendor data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVendorData();
  }, [searchParams, location.state, params.vendorId, navigate, user]);

  // Track viewing history in database (with localStorage fallback)
  useEffect(() => {
    const trackView = async () => {
      if (!vendor) return;

      try {
        // Track in database if user is logged in
        if (user?.id) {
          const result = await viewingHistoryService.trackView(user.id, vendor.id);
          if (!result.success) {
            console.warn('Failed to track view in database:', result.error);
            // Fallback to localStorage
            trackViewInLocalStorage();
          }
        } else {
          // For guest users, use localStorage
          trackViewInLocalStorage();
        }
      } catch (error) {
        console.error('Error tracking view:', error);
        trackViewInLocalStorage();
      }
    };

    const trackViewInLocalStorage = () => {
      try {
        const userId = user?.id || 'guest';
        const viewingHistory = localStorage.getItem(`viewingHistory_${userId}`);
        let history = viewingHistory ? JSON.parse(viewingHistory) : [];

        const newView = {
          id: vendor.id,
          name: vendor.name,
          category: vendor.category || 'General',
          location: vendor.location,
          rating: vendor.rating,
          reviewCount: vendor.reviewCount,
          startingPrice: vendor.pricing?.startingPrice || 0,
          image: vendor.gallery?.[0] || vendor.profileImage || `https://ui-avatars.com/api/?name=${vendor.name}&background=8B1538&color=fff&size=300`,
          viewedAt: new Date().toISOString(),
          verified: vendor.isVerified
        };

        history = history.filter(v => v.id !== vendor.id);
        history.unshift(newView);
        history = history.slice(0, 20);

        localStorage.setItem(`viewingHistory_${userId}`, JSON.stringify(history));
      } catch (error) {
        console.error('Error tracking view in localStorage:', error);
      }
    };

    trackView();
  }, [vendor, user]);

  // Use actual vendor data - no mock fallback
  const displayVendor = vendor || {
    id: '550e8400-e29b-41d4-a716-446655440000', // Fixed: Use proper UUID format instead of 'vendor-001'
    name: 'Rajesh Kumar Photography',
    rating: 4.8,
    reviewCount: 127,
    bookingCount: 89,
    location: 'San Francisco Bay Area, CA',
    serviceRadius: 50,
    businessAddress: '1234 Mission Street, San Francisco, CA 94103',
    coordinates: { lat: 37.7749, lng: -122.4194 },
    responseTime: '2 hours',
    isVerified: true,
    isAvailable: true,
    description: `Award-winning South Asian wedding photographer specializing in capturing the vibrant traditions and intimate moments of multicultural celebrations. With over 8 years of experience, I blend traditional photojournalism with contemporary artistry to tell your unique love story.\n\nMy approach focuses on authentic emotions, cultural authenticity, and creating timeless memories that honor both tradition and modernity. I understand the significance of each ritual and ceremony, ensuring every precious moment is beautifully preserved.`,
    services: [
      'Wedding Photography',
      'Engagement Sessions',
      'Pre-Wedding Shoots',
      'Sangeet Photography',
      'Mehndi Ceremonies',
      'Reception Coverage',
      'Destination Weddings',
      'Family Portraits'
    ],
    culturalSpecializations: [
      { name: 'Punjabi Weddings', description: 'Sikh ceremonies and traditions' },
      { name: 'Hindu Ceremonies', description: 'Traditional rituals and customs' },
      { name: 'Gujarati Weddings', description: 'Gujarati customs and celebrations' },
      { name: 'Interfaith Ceremonies', description: 'Mixed cultural celebrations' }
    ],
    languages: ['English', 'Hindi', 'Punjabi', 'Gujarati'],
    pricing: {
      startingPrice: 3500,
      description: 'Full day wedding coverage with edited gallery',
      duration: '8-10 hours',
      guestCount: 300
    },
    tags: [
      'Verified',
      'Female-Owned',
      'Free Consultation',
      'Offers Trials',
      'Travel-Ready',
      'Available This Weekend',
      '5-Star Reviews',
      'Recently Booked'
    ],
    gallery: [
      'https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg',
      'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg',
      'https://images.pexels.com/photos/1729931/pexels-photo-1729931.jpeg',
      'https://images.pexels.com/photos/1024994/pexels-photo-1024994.jpeg',
      'https://images.pexels.com/photos/1444443/pexels-photo-1444443.jpeg',
      'https://images.pexels.com/photos/1729932/pexels-photo-1729932.jpeg',
      'https://images.pexels.com/photos/1024995/pexels-photo-1024995.jpeg',
      'https://images.pexels.com/photos/144/pexels-photo-144.jpeg'
    ],
    availability: {
      availableDates: [
        '2024-02-15', '2024-02-16', '2024-02-17', '2024-02-22', '2024-02-23',
        '2024-02-24', '2024-03-01', '2024-03-02', '2024-03-08', '2024-03-09',
        '2024-03-15', '2024-03-16', '2024-03-22', '2024-03-23', '2024-03-29'
      ],
      bookedDates: [
        '2024-02-10', '2024-02-11', '2024-02-18', '2024-02-25',
        '2024-03-03', '2024-03-10', '2024-03-17', '2024-03-24'
      ]
    },
    reviews: [
      {
        id: 1,
        clientName: 'Priya & Arjun Sharma',
        clientPhoto: 'https://randomuser.me/api/portraits/women/32.jpg',
        rating: 5,
        date: 'January 2024',
        culture: 'Punjabi',
        eventType: 'Wedding',
        comment: `Rajesh exceeded all our expectations! He captured every moment of our three-day Punjabi wedding celebration with such artistry and cultural sensitivity. The photos are absolutely stunning - from the intimate Mehndi ceremony to the grand reception. He understood the significance of each ritual and made sure to capture all the important family moments. Highly recommend for any South Asian wedding!`,
        photos: [
          'https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg',
          'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg'
        ],
        helpfulCount: 24,
        vendorResponse: {
          date: 'January 2024',
          message: 'Thank you so much Priya and Arjun! It was an absolute honor to be part of your beautiful celebration. Your families were so welcoming and the love between you two was evident in every shot. Wishing you both a lifetime of happiness!'
        }
      },
      {
        id: 2,
        clientName: 'Sarah & Vikram Patel',
        clientPhoto: 'https://randomuser.me/api/portraits/women/45.jpg',
        rating: 5,
        date: 'December 2023',
        culture: 'Gujarati',
        eventType: 'Engagement',
        comment: `As an interfaith couple, we were looking for a photographer who could beautifully blend both our traditions. Rajesh was perfect! He captured our Gujarati engagement ceremony and American reception with equal skill and respect. The photos tell our story perfectly - showing both cultures coming together in celebration.`,
        photos: [],
        helpfulCount: 18,
        vendorResponse: null
      },
      {
        id: 3,
        clientName: 'Meera & David Johnson',
        clientPhoto: 'https://randomuser.me/api/portraits/women/28.jpg',
        rating: 4,
        date: 'November 2023',
        culture: 'Hindu',
        eventType: 'Wedding',
        comment: `Rajesh did a wonderful job capturing our Hindu wedding ceremony. He was professional, punctual, and really understood the importance of each ritual. The only minor issue was some delay in receiving the final edited photos, but the quality was worth the wait. Would definitely recommend him to other couples.`,
        photos: [
          'https://images.pexels.com/photos/1729931/pexels-photo-1729931.jpeg'
        ],
        helpfulCount: 12,
        vendorResponse: {
          date: 'November 2023',
          message: 'Thank you Meera and David! I apologize for the delay in delivery - we had an unusually busy season. I\'m glad you loved the final results and appreciate your patience. Congratulations again on your beautiful wedding!'
        }
      }
    ],
    portfolio: [
      {
        id: 1,
        image: 'https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg',
        title: 'Punjabi Wedding Ceremony',
        category: 'wedding',
        eventInfo: { couple: 'Priya & Arjun', location: 'Fremont Gurdwara' }
      },
      {
        id: 2,
        image: 'https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg',
        title: 'Mehndi Night Celebration',
        category: 'mehndi',
        eventInfo: { couple: 'Kavya & Rohit', location: 'San Jose' }
      },
      {
        id: 3,
        image: 'https://images.pexels.com/photos/1729931/pexels-photo-1729931.jpeg',
        title: 'Sangeet Dance Performance',
        category: 'sangeet',
        eventInfo: { couple: 'Anita & Raj', location: 'Milpitas' }
      },
      {
        id: 4,
        image: 'https://images.pexels.com/photos/1024994/pexels-photo-1024994.jpeg',
        title: 'Hindu Wedding Rituals',
        category: 'wedding',
        eventInfo: { couple: 'Meera & David', location: 'Sunnyvale Temple' }
      },
      {
        id: 5,
        image: 'https://images.pexels.com/photos/1444443/pexels-photo-1444443.jpeg',
        title: 'Haldi Ceremony',
        category: 'haldi',
        eventInfo: { couple: 'Sonia & Amit', location: 'Cupertino' }
      },
      {
        id: 6,
        image: 'https://images.pexels.com/photos/1729932/pexels-photo-1729932.jpeg',
        title: 'Reception Celebration',
        category: 'reception',
        eventInfo: { couple: 'Riya & Michael', location: 'San Francisco' }
      }
    ],
    packages: [
      {
        id: 'basic',
        name: 'Essential Coverage',
        price: 2500,
        duration: '6 hours',
        guestCount: 150,
        isPopular: false,
        advanceBooking: '2 months',
        travelCharges: 'Included within 25 miles',
        culturalSpecializations: ['Hindu', 'Sikh'],
        features: [
          { name: 'Professional photographer', included: true },
          { name: '6 hours of coverage', included: true },
          { name: '300+ edited photos', included: true },
          { name: 'Online gallery access', included: true },
          { name: 'Basic photo editing', included: true },
          { name: 'Second photographer', included: false },
          { name: 'Engagement session', included: false },
          { name: 'Same-day preview', included: false }
        ]
      },
      {
        id: 'premium',
        name: 'Complete Wedding',
        price: 3500,
        duration: '8-10 hours',
        guestCount: 300,
        isPopular: true,
        advanceBooking: '3 months',
        travelCharges: 'Included within 50 miles',
        culturalSpecializations: ['Hindu', 'Sikh', 'Gujarati', 'Interfaith'],
        features: [
          { name: 'Lead photographer + assistant', included: true },
          { name: '8-10 hours of coverage', included: true },
          { name: '500+ edited photos', included: true },
          { name: 'Online gallery access', included: true },
          { name: 'Professional photo editing', included: true },
          { name: 'Engagement session (2 hours)', included: true },
          { name: 'Same-day preview (10 photos)', included: true },
          { name: 'USB drive with all photos', included: true }
        ]
      },
      {
        id: 'luxury',
        name: 'Platinum Experience',
        price: 5000,
        duration: '12+ hours',
        guestCount: 500,
        isPopular: false,
        advanceBooking: '4 months',
        travelCharges: 'Included within 100 miles',
        culturalSpecializations: ['All South Asian Cultures', 'Destination Weddings'],
        features: [
          { name: 'Team of 2 photographers', included: true },
          { name: 'Full day coverage (12+ hours)', included: true },
          { name: '800+ edited photos', included: true },
          { name: 'Premium online gallery', included: true },
          { name: 'Advanced photo editing & retouching', included: true },
          { name: 'Engagement + pre-wedding session', included: true },
          { name: 'Same-day preview (25 photos)', included: true },
          { name: 'Custom photo album', included: true },
          { name: 'Drone photography (if permitted)', included: true }
        ]
      }
    ],
    nearbyLandmarks: [
      { name: 'Golden Gate Bridge', distance: '8 miles', type: 'Landmark' },
      { name: 'San Francisco Airport', distance: '12 miles', type: 'Airport' },
      { name: 'Fremont Hindu Temple', distance: '25 miles', type: 'Temple' },
      { name: 'Gurdwara Sahib Fremont', distance: '28 miles', type: 'Gurdwara' }
    ],
    contactInfo: {
      phone: '+1 (415) 555-0123',
      email: 'rajesh@rkphotography.com'
    }
  };

  const breadcrumbs = [
    { label: 'Home', path: '/homepage', key: 'home' },
    { label: 'Photographers', path: '/category-browse', key: 'category' },
    { label: 'Bay Area', path: '/category-browse', key: 'location' },
    { label: vendor?.name, path: location?.pathname, key: 'vendor' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'portfolio', label: 'Portfolio', icon: 'Camera' },
    { id: 'reviews', label: 'Reviews', icon: 'Star' },
    { id: 'pricing', label: 'Pricing', icon: 'DollarSign' },
    { id: 'availability', label: 'Availability', icon: 'Calendar' },
    { id: 'location', label: 'Location', icon: 'MapPin' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleShare = async () => {
    setShareStatus(null);

    try {
      const result = await shareContent({
        title: `${vendor?.name} - ShaadiNest`,
        text: `Check out ${vendor?.name} on ShaadiNest`,
        url: window.location?.href,
        fallbackMessage: 'Vendor profile link copied to clipboard!'
      }, {
        onSuccess: ({ method, message }) => {
          setShareStatus({ type: 'success', message: message || 'Shared successfully!' });
          setTimeout(() => setShareStatus(null), 3000);
        },
        onError: ({ error }) => {
          setShareStatus({ type: 'error', message: 'Unable to share. Please try again.' });
          setTimeout(() => setShareStatus(null), 3000);
        }
      });
    } catch (error) {
      console.error('Share error:', error);
      setShareStatus({ type: 'error', message: 'Unable to share. Please try again.' });
      setTimeout(() => setShareStatus(null), 3000);
    }
  };

  const handleToggleFavorite = async () => {
    // Check if user is authenticated
    if (!user) {
      navigate('/sign-in-page', {
        state: { returnTo: location?.pathname, action: 'save' }
      });
      return;
    }

    if (!vendor?.id) return;

    try {
      // Optimistically update UI
      setIsFavorited(!isFavorited);

      // Toggle saved status in database
      const result = await savedVendorsService.toggleSaveVendor(user.id, vendor.id);

      if (!result.success) {
        // Revert on error
        setIsFavorited(isFavorited);
        showToast('Failed to save vendor. Please try again.', 'error');
      } else {
        showToast(isFavorited ? 'Vendor removed from favorites' : 'Vendor saved to favorites', 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert on error
      setIsFavorited(isFavorited);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleCheckAvailability = (date) => {
    // Check if user is authenticated
    if (!user) {
      navigate('/sign-in-page', {
        state: { returnTo: location?.pathname, action: 'checkAvailability' }
      });
      return;
    }

    // Set selected date and open message modal with pre-filled information
    setSelectedDate(date);
    setMessageForm(prev => ({
      ...prev,
      subject: `Availability Inquiry for ${date}`,
      contactInfo: {
        ...prev.contactInfo,
        preferredDate: date,
        eventDate: date
      }
    }));
    setShowMessageModal(true);
  };

  const handleBookNow = (packageData = null) => {
    // Check if user is authenticated
    if (!user) {
      navigate('/sign-in-page', {
        state: { returnTo: location?.pathname, action: 'book' }
      });
      return;
    }

    // Set selected package if provided
    if (packageData) {
      setSelectedPackage(packageData);
    }

    // Open booking modal
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    setBookingSubmitting(true);

    try {
      const result = await bookingService.createBooking({
        vendorId: vendor.id,
        userId: user.id,
        packageId: bookingData.packageId || null,
        eventDate: bookingData.eventDate,
        eventTime: bookingData.eventTime || null,
        eventLocation: bookingData.eventLocation,
        guestCount: bookingData.guestCount || null,
        totalAmount: bookingData.totalAmount,
        specialRequirements: bookingData.specialRequirements || null,
        eventType: bookingData.eventType
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create booking');
      }

      showToast('🎉 Booking request submitted successfully! The vendor will contact you within 24-48 hours.', 'success', 5000);
      setShowBookingModal(false);
      setSelectedPackage(null);

      // Optionally navigate to user dashboard bookings
      setTimeout(() => {
        navigate('/user-dashboard', { state: { tab: 'bookings' } });
      }, 2000);

    } catch (error) {
      console.error('Booking error:', error);
      showToast(`Failed to submit booking: ${error.message}`, 'error');
    } finally {
      setBookingSubmitting(false);
    }
  };

  const handleMessage = () => {
    // Check if user is authenticated
    if (!user) {
      navigate('/sign-in-page', {
        state: { returnTo: location?.pathname, action: 'message' }
      });
      return;
    }

    // Open message modal
    setShowMessageModal(true);
    setMessageStatus(null);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setMessageForm({
      subject: '',
      message: '',
      contactInfo: {
        phone: '',
        preferredDate: '',
        eventDate: '',
        guestCount: '',
        budget: ''
      }
    });
    setMessageStatus(null);
  };

  const handleMessageFormChange = (field, value) => {
    if (field?.includes('.')) {
      const [parent, child] = field?.split('.');
      setMessageForm(prev => ({
        ...prev,
        [parent]: {
          ...prev?.[parent],
          [child]: value
        }
      }));
    } else {
      setMessageForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();

    if (!messageForm?.message?.trim()) {
      setMessageStatus({ type: 'error', message: 'Please enter your message' });
      return;
    }

    // Check authentication
    if (!user?.id) {
      navigate('/sign-in-page', {
        state: { returnTo: location?.pathname }
      });
      return;
    }

    if (!vendor?.id) {
      setMessageStatus({ type: 'error', message: 'Vendor information not available' });
      return;
    }

    setMessageSending(true);
    setMessageStatus(null);

    try {
      // Prepare inquiry data for Supabase
      const inquiryData = {
        user_id: user.id,
        vendor_id: vendor.id,
        subject: messageForm.subject || `Inquiry about ${vendor.name}`,
        message: messageForm.message,
        event_date: messageForm.contactInfo?.eventDate || null,
        guest_count: messageForm.contactInfo?.guestCount ? parseInt(messageForm.contactInfo.guestCount) : null,
        budget: messageForm.contactInfo?.budget ? parseFloat(messageForm.contactInfo.budget) : null,
        phone: messageForm.contactInfo?.phone || user.phone || '',
        status: 'pending'
      };

      // Send the inquiry to Supabase
      const response = await inquiryService.createInquiry(inquiryData);

      if (!response?.success) {
        throw new Error(response?.error || 'Failed to send message');
      }

      setMessageStatus({
        type: 'success',
        message: 'Your inquiry has been sent successfully! The vendor will respond within 24 hours.'
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        handleCloseMessageModal();
      }, 2000);

    } catch (error) {
      console.error('Message sending error:', error);
      setMessageStatus({
        type: 'error',
        message: error?.message || 'Failed to send message. Please try again.'
      });
    } finally {
      setMessageSending(false);
    }
  };

  const handleBookVendor = () => {
    if (!user) {
      navigate('/sign-in-page');
      return;
    }
    setShowMessageModal(true);
  };

  const handleRequestQuote = (packageId) => {
    if (!user) {
      navigate('/sign-in-page');
      return;
    }
    setShowMessageModal(true);
    setMessageForm(prev => ({
      ...prev,
      subject: `Quote Request for Package #${packageId}`
    }));
  };

  const handleBookPackage = (pkg) => {
    if (!user) {
      navigate('/sign-in-page');
      return;
    }
    navigate('/booking-request', {
      state: {
        vendor: vendor,
        package: pkg
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Vendor Not Found */}
      {!loading && !vendor && (
        <div className="container mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold mb-4">Vendor Not Found</h2>
          <p className="text-text-secondary mb-6">The vendor you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/category-browse')}
            className="btn-primary"
          >
            Browse All Vendors
          </button>
        </div>
      )}

      {/* Share Status Toast */}
      {!loading && vendor && shareStatus && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-strong animate-slide-down ${shareStatus?.type === 'success' ? 'bg-success text-white' : 'bg-error text-white'
          }`}>
          <div className="flex items-center space-x-2">
            <Icon
              name={shareStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
              size={16}
              color="currentColor"
            />
            <span className="text-sm font-medium">{shareStatus?.message}</span>
          </div>
        </div>
      )}
      {/* Message Modal */}
      {!loading && vendor && showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="font-heading font-bold text-xl text-text-primary">
                  Send Message to {vendor?.name}
                </h2>
                <p className="text-text-secondary font-caption mt-1">
                  Get a personalized quote and discuss your wedding needs
                </p>
              </div>
              <button
                onClick={handleCloseMessageModal}
                className="p-2 hover:bg-surface-50 rounded-lg transition-smooth"
                disabled={messageSending}
              >
                <Icon name="X" size={24} color="#6B5B4F" />
              </button>
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSendMessage} className="p-6 space-y-6">
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Subject <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={messageForm?.subject}
                  onChange={(e) => handleMessageFormChange('subject', e?.target?.value)}
                  placeholder="e.g., Wedding Photography Inquiry"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                />
              </div>

              {/* Contact Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={messageForm?.contactInfo?.phone}
                    onChange={(e) => handleMessageFormChange('contactInfo.phone', e?.target?.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Wedding Date
                  </label>
                  <input
                    type="date"
                    value={messageForm?.contactInfo?.eventDate}
                    onChange={(e) => handleMessageFormChange('contactInfo.eventDate', e?.target?.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Guest Count
                  </label>
                  <select
                    value={messageForm?.contactInfo?.guestCount}
                    onChange={(e) => handleMessageFormChange('contactInfo.guestCount', e?.target?.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  >
                    <option value="">Select guest count</option>
                    <option value="50-100">50-100 guests</option>
                    <option value="100-200">100-200 guests</option>
                    <option value="200-300">200-300 guests</option>
                    <option value="300-500">300-500 guests</option>
                    <option value="500+">500+ guests</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Budget Range
                  </label>
                  <select
                    value={messageForm?.contactInfo?.budget}
                    onChange={(e) => handleMessageFormChange('contactInfo.budget', e?.target?.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth"
                  >
                    <option value="">Select budget range</option>
                    <option value="$1,000-$2,500">$1,000 - $2,500</option>
                    <option value="$2,500-$5,000">$2,500 - $5,000</option>
                    <option value="$5,000-$10,000">$5,000 - $10,000</option>
                    <option value="$10,000+">$10,000+</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Your Message <span className="text-error">*</span>
                </label>
                <textarea
                  value={messageForm?.message}
                  onChange={(e) => handleMessageFormChange('message', e?.target?.value)}
                  placeholder="Tell us about your wedding plans, specific requirements, questions about packages, or any other details..."
                  rows={6}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth resize-vertical"
                  required
                />
                <p className="text-text-secondary text-sm mt-1">
                  {messageForm?.message?.length}/1000 characters
                </p>
              </div>

              {/* Status Message */}
              {messageStatus && (
                <div className={`p-4 rounded-lg flex items-center space-x-3 ${messageStatus?.type === 'success' ? 'bg-success-50 text-success border border-success-200' : 'bg-error-50 text-error border border-error-200'
                  }`}>
                  <Icon
                    name={messageStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                    size={20}
                    color="currentColor"
                  />
                  <span className="font-medium">{messageStatus?.message}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={handleCloseMessageModal}
                  disabled={messageSending}
                  className="px-6 py-3 text-text-secondary hover:text-text-primary border border-border hover:border-border-dark rounded-lg transition-smooth disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={messageSending || !messageForm?.message?.trim()}
                  className="px-8 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {messageSending && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  <span>{messageSending ? 'Sending...' : 'Send Message'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Breadcrumbs */}
      {!loading && vendor && (
        <>
          <div className="bg-surface-50 border-b border-border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <nav className="flex items-center space-x-2 text-sm">
                {breadcrumbs?.map((crumb, index) => (
                  <React.Fragment key={crumb?.key}>
                    {index === breadcrumbs?.length - 1 ? (
                      <span className="text-text-primary font-medium">{crumb?.label}</span>
                    ) : (
                      <>
                        <Link
                          to={crumb?.path}
                          className="text-text-secondary hover:text-primary transition-smooth font-caption"
                        >
                          {crumb?.label}
                        </Link>
                        <Icon name="ChevronRight" size={14} color="#8B7B6F" />
                      </>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Hero Gallery */}
                <VendorHero
                  vendor={vendor}
                  onShare={handleShare}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorited={isFavorited}
                />

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl border border-border shadow-soft">
                  <div className="border-b border-border-light">
                    <nav className="flex space-x-8 px-6">
                      {tabs?.map((tab) => (
                        <button
                          key={tab?.id}
                          onClick={() => setActiveTab(tab?.id)}
                          className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-smooth ${activeTab === tab?.id
                            ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-primary hover:border-border-dark'
                            }`}
                        >
                          <Icon name={tab?.icon} size={16} color="currentColor" />
                          <span>{tab?.label}</span>
                        </button>
                      ))}
                    </nav>
                  </div>

                  <div className="p-6">
                    {activeTab === 'overview' && <VendorInfo vendor={vendor} />}
                    {activeTab === 'portfolio' && <PortfolioGallery vendor={vendor} />}
                    {activeTab === 'reviews' && <ReviewsSection vendor={vendor} />}
                    {activeTab === 'pricing' && (
                      <PricingPackages
                        vendor={vendor}
                        onRequestQuote={handleRequestQuote}
                        onBookPackage={handleBookPackage}
                        onToggleComparison={() => { }}
                      />
                    )}
                    {activeTab === 'availability' && (
                      <AvailabilityCalendar
                        vendor={vendor}
                        onDateSelect={handleDateSelect}
                        onCheckAvailability={handleCheckAvailability}
                      />
                    )}
                    {activeTab === 'location' && <LocationMap vendor={vendor} />}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Info Card */}
                <div className="bg-white rounded-xl border border-border shadow-soft p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Camera" size={32} color="#8B4513" />
                    </div>
                    <h2 className="font-heading font-bold text-xl text-text-primary mb-2">
                      {vendor?.name}
                    </h2>
                    <div className="flex items-center justify-center space-x-1 mb-2">
                      <Icon name="Star" size={16} color="#DAA520" strokeWidth={0} className="fill-current" />
                      <span className="font-medium text-text-primary">{vendor?.rating}</span>
                      <span className="text-text-secondary font-caption">
                        ({vendor?.reviewCount} reviews)
                      </span>
                    </div>
                    <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary">
                      <Icon name="MapPin" size={14} color="currentColor" />
                      <span className="font-caption">{vendor?.location}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-text-secondary font-caption">Starting Price</span>
                      <span className="font-heading font-bold text-lg text-primary">
                        ${vendor?.pricing?.startingPrice?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-secondary font-caption">Response Time</span>
                      <span className="text-text-primary font-medium">{vendor?.responseTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-secondary font-caption">Availability</span>
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${vendor?.isAvailable ? 'bg-success' : 'bg-warning'}`} />
                        <span className="text-text-primary font-medium">
                          {vendor?.isAvailable ? 'Available' : 'Busy'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl border border-border shadow-soft p-6">
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleMessage}
                      className="w-full flex items-center space-x-3 p-3 bg-primary-50 hover:bg-primary-100 rounded-lg transition-smooth"
                    >
                      <Icon name="MessageCircle" size={18} color="#8B4513" />
                      <span className="font-medium text-primary">Send Message</span>
                    </button>
                    <button
                      onClick={handleCheckAvailability}
                      className="w-full flex items-center space-x-3 p-3 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-smooth"
                    >
                      <Icon name="Calendar" size={18} color="#DAA520" />
                      <span className="font-medium text-secondary">Check Availability</span>
                    </button>
                    <button
                      onClick={() => handleBookNow()}
                      className="w-full flex items-center space-x-3 p-3 bg-success-50 hover:bg-success-100 rounded-lg transition-smooth"
                    >
                      <Icon name="BookOpen" size={18} color="#228B22" />
                      <span className="font-medium text-success">Book Now</span>
                    </button>
                  </div>
                </div>

                {/* Cultural Specializations */}
                <div className="bg-white rounded-xl border border-border shadow-soft p-6">
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                    Cultural Expertise
                  </h3>
                  <div className="space-y-2">
                    {vendor?.culturalSpecializations?.map((culture) => (
                      <div key={culture?.name} className="flex items-center space-x-2">
                        <Icon name="Heart" size={14} color="#DAA520" />
                        <span className="text-sm text-text-primary font-medium">{culture?.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-white rounded-xl border border-border shadow-soft p-6">
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Icon name="Phone" size={16} color="#6B5B4F" />
                      <span className="text-text-primary font-data">{vendor?.contactInfo?.phone}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="Mail" size={16} color="#6B5B4F" />
                      <span className="text-text-primary font-caption">{vendor?.contactInfo?.email}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Icon name="MapPin" size={16} color="#6B5B4F" />
                      <span className="text-text-secondary font-caption">{vendor?.businessAddress}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Vendor Action Bar */}
          <VendorActionBar
            vendorId={vendor?.id}
            vendorName={vendor?.name}
            isAvailable={vendor?.isAvailable}
            isFavorited={isFavorited}
            onMessage={handleMessage}
            onCheckAvailability={handleCheckAvailability}
            onBookNow={handleBookNow}
            onToggleFavorite={handleToggleFavorite}
            onShare={handleShare}
            contactInfo={vendor?.contactInfo}
          />

          {/* Booking Modal */}
          <BookingModal
            vendor={vendor}
            isOpen={showBookingModal}
            onClose={() => {
              setShowBookingModal(false);
              setSelectedPackage(null);
            }}
            onSubmit={handleBookingSubmit}
            isSubmitting={bookingSubmitting}
            selectedPackage={selectedPackage}
          />
        </>
      )}

      <Footer />
    </div>
  );
};

export default VendorProfile;